import React, { useEffect, useState } from "react";
import { Button, Card, Menu, Modal, Select, Table } from "antd";
import { EditOutlined, PlusOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { collection, getDocs } from "firebase/firestore";
import { firestore } from "../../../../configs/FirebaseConfig";
import { FIRESTORE_COUNTRIES_TABLE, FIRESTORE_REGIONS_TABLE } from "../../../../constants/FirestoreConstant";
import { COMPANY_COUNTRY_CODE } from "../../../../constants/CompanyConstants";
import PageHeaderAlt from "../../../../components/layout-components/PageHeaderAlt";
import Flex from "../../../../components/shared-components/Flex";
import EllipsisDropdown from "../../../../components/shared-components/EllipsisDropdown";
import urls from "urls";

const { Option } = Select;

const CountryList = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalVisible, setModalVisible] = useState(false);
  const [selectedCode, setSelectedCode] = useState(null);

  const NL_COUNTRIES = [
    { code: "NL", label: t("netherlands") },
    { code: "BE", label: t("belgium") },
    { code: "DE", label: t("germany") },
    { code: "FR", label: t("france") },
    { code: "LU", label: t("luxembourg") },
  ];

  useEffect(() => {
    const fetchCountries = async () => {
      try {
        const colRef = collection(firestore, FIRESTORE_REGIONS_TABLE, COMPANY_COUNTRY_CODE, FIRESTORE_COUNTRIES_TABLE);
        const snapshot = await getDocs(colRef);
        const docs = snapshot.docs.map((doc) => ({ countryCode: doc.id, ...doc.data() }));
        setCountries(docs);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchCountries();
  }, []);

  const existingCodes = countries.map((c) => c.countryCode);
  const availableToAdd = NL_COUNTRIES.filter((c) => !existingCodes.includes(c.code));

  const handleModalConfirm = () => {
    if (selectedCode) {
      setModalVisible(false);
      navigate(urls.addCountry + selectedCode);
    }
  };

  const handleModalCancel = () => {
    setModalVisible(false);
    setSelectedCode(null);
  };

  const dropdownMenu = (record) => (
    <Menu>
      <Menu.Item>
        <a onClick={() => navigate(urls.editCountry + record.countryCode)}>
          <Flex alignItems="center">
            <EditOutlined />
            <span style={{ marginLeft: 4 }}>{t("edit")}</span>
          </Flex>
        </a>
      </Menu.Item>
    </Menu>
  );

  const columns = [
    {
      title: t("countries.country_code"),
      dataIndex: "countryCode",
      key: "countryCode",
      render: (code, record) => {
        const country = NL_COUNTRIES.find((c) => c.code === code);
        return (
          <button className={"text-button"} onClick={() => navigate(urls.editCountry + record.countryCode)}>
            <span>{country ? `${code} — ${country.label}` : code}</span>
          </button>
        );
      },
    },
    {
      title: "",
      key: "actions",
      width: 100,
      align: "right",
      render: (_, record) => (
        <div
          className="text-right"
          style={{ display: "flex", alignItems: "center", justifyContent: "flex-end", gap: "3px" }}
        >
          <EllipsisDropdown menu={dropdownMenu(record)} />
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeaderAlt className="border-bottom" overlap>
        <div className="container">
          <Flex className="py-2" mobileFlex={false} justifyContent="between" alignItems="center">
            <h2 className="mb-3">{t("countries.title")}</h2>
            {availableToAdd.length > 0 && (
              <div className="mb-3">
                <Button onClick={() => setModalVisible(true)}>
                  <PlusOutlined style={{ marginRight: 8 }} />
                  {t("countries.add_country")}
                </Button>
              </div>
            )}
          </Flex>
        </div>
      </PageHeaderAlt>

      <div className="container" style={{ marginTop: 30 }}>
        <Card>
          <Table columns={columns} dataSource={countries} rowKey="countryCode" loading={loading} pagination={false} />
        </Card>
      </div>

      <Modal
        title={t("countries.add_country")}
        open={modalVisible}
        onOk={handleModalConfirm}
        onCancel={handleModalCancel}
        okButtonProps={{ disabled: !selectedCode }}
        destroyOnClose
      >
        <Select
          style={{ width: "100%", marginTop: 8 }}
          placeholder={t("countries.select_country_to_add")}
          onChange={(val) => setSelectedCode(val)}
          value={selectedCode}
        >
          {availableToAdd.map((country) => (
            <Option key={country.code} value={country.code}>
              {country.label}
            </Option>
          ))}
        </Select>
      </Modal>
    </>
  );
};

export default CountryList;
